import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { useAuth } from '@gbs-monorepo-packages/auth';
import {
  FormBase,
  FormModal,
  type IRole,
  Roles,
  SelectData,
  UploadImageModal,
  clientAndEmployeeWithLevel,
  rolesWithLevel,
  rolesWithoutAdminWithLevel,
  translateUserRoleInRole,
} from '@gbs-monorepo-packages/common';
import { zodResolver } from '@hookform/resolvers/zod';

import { CLIENT_ID } from '../../../../constants/Env';
import {
  type UsersCreateSchema,
  userCreateSchema,
} from '../../../../formSchemas/userSchema';
import { type IClientDTO } from '../../../../services/companies';

interface IAddTeammatesProps {
  onAccept: (data: UsersCreateSchema) => Promise<boolean>;
  onDecline: () => void;
  open: boolean;
  loading: boolean;
  success?: boolean;
  client: IClientDTO;
}

const getCurrentRole = (userRoles: string[]): IRole | null => {
  let currentRole = null;
  for (const value of userRoles.values()) {
    const userRoleAux = translateUserRoleInRole[value] ?? null;
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    if (!userRoleAux) {
      continue;
    }

    if (!currentRole) {
      currentRole = userRoleAux;
    } else if (currentRole.level < userRoleAux.level) {
      currentRole = userRoleAux;
    }
  }

  return currentRole;
};

const MAIN_CLIENT_ID_NUM = Number(CLIENT_ID);

export const ModalAddUsers = ({
  onAccept,
  onDecline,
  open,
  loading = false,
  success = false,
  client,
}: IAddTeammatesProps): JSX.Element => {
  const isSelectOpen = useRef(new Set());
  const { user } = useAuth();
  const { clientId = '' } = useParams();
  const selectData = useRef(null);
  const [imageFile, setImageFile] = useState<File | null>(null);

  const createUsers = useForm<UsersCreateSchema>({
    resolver: zodResolver(userCreateSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      roles: Roles.EMPLOYEE,
      clientId: Number(clientId),
      phone: '',
    },
  });

  const {
    reset,
    formState: { errors },
    setValue,
    watch,
    handleSubmit,
  } = createUsers;

  const values = watch();

  const handleRoleInviteSelectChange = (value: string) => {
    setValue('roles', value);
  };

  const resetForm = useCallback(() => {
    reset();
  }, [reset]);

  const handleDeclineAddTeammates = useCallback(() => {
    if (!isSelectOpen.current.size) {
      resetForm();
      onDecline?.();
    }
  }, [onDecline, resetForm]);

  useEffect(() => {
    if (success) {
      resetForm();
    }
  }, [success]);

  const handleOpenChange = useCallback((isOpen: boolean, key: string) => {
    if (isOpen) {
      isSelectOpen.current.add(key);
    } else {
      isSelectOpen.current.delete(key);
    }
  }, []);

  const onSubmit: SubmitHandler<UsersCreateSchema> = async (
    data: UsersCreateSchema
  ) => {
    const userAux: UsersCreateSchema = {
      ...data,
    };

    if (imageFile) {
      userAux.profile = imageFile;
    }

    await onAccept?.(userAux).then(() => {
      resetForm();
    });
  };

  const currentRole = useMemo(() => {
    const userRoles = user?.roles ?? [];
    if (!userRoles.length) {
      return null;
    }

    return getCurrentRole(userRoles);
  }, [user]);

  const rolesAux = useMemo(() => {
    if (currentRole !== null) {
      let roleAuxToFilter;

      if (Number(clientId) !== MAIN_CLIENT_ID_NUM) {
        roleAuxToFilter =
          client?.isAgency === true
            ? rolesWithoutAdminWithLevel
            : clientAndEmployeeWithLevel;
      } else {
        roleAuxToFilter = rolesWithLevel;
      }

      return roleAuxToFilter.filter(
        ({ level }) => level <= (currentRole.level ?? 0)
      );
    } else {
      return [];
    }
  }, [currentRole, clientId, client]);

  return !open ? (
    <></>
  ) : (
    <FormBase.Provider {...createUsers}>
      <FormModal
        acceptText="Add User"
        declineText="Cancel"
        open={open}
        biggerModal
        dataCy="title-newUser"
        mainText="New User"
        onOpenChange={handleDeclineAddTeammates}
        onDecline={handleDeclineAddTeammates}
        onAccept={handleSubmit(onSubmit)}
        loading={loading}
        formId="add-clientForm"
        zIndex={10}
      >
        <FormBase.Content>
          <UploadImageModal
            label="User Image"
            onSelectImage={(file) => {
              setImageFile(file);
            }}
            onRemoveImagePreview={() => {
              setImageFile(null);
            }}
            loadingDeleteImage={loading}
            loading={loading}
          />
          <FormBase.Grid>
            <FormBase.InputContent
              filled={!!values?.firstName}
              inputRef="firstName"
              label="First Name"
              errorMessage={errors.firstName?.message}
              dataCy="label-firstName"
            >
              <FormBase.InputText
                dataCy="input-firstName"
                id="firstName"
                name="firstName"
                type="text"
                autoComplete="off"
                maxLength={30}
                disabled={loading}
              />
            </FormBase.InputContent>

            <FormBase.InputContent
              filled={!!values?.lastName}
              inputRef="lastName"
              label="Last Name"
              errorMessage={errors.lastName?.message}
              dataCy="label-lastName"
            >
              <FormBase.InputText
                dataCy="input-lastName"
                id="lastName"
                name="lastName"
                type="text"
                maxLength={30}
                disabled={loading}
              />
            </FormBase.InputContent>
          </FormBase.Grid>

          <FormBase.InputContent
            filled={!!values?.email}
            inputRef="emailInvite"
            label="Email"
            errorMessage={errors.email?.message}
            dataCy="label-email"
          >
            <FormBase.InputText
              dataCy="input-email"
              id="emailInvite"
              name="email"
              type="text"
              maxLength={40}
              disabled={loading}
            />
          </FormBase.InputContent>

          <FormBase.InputContent
            filled={!!values?.phone}
            inputRef="phoneInvite"
            label="Phone"
            errorMessage={errors.phone?.message}
            dataCy="label-phone"
          >
            <FormBase.InputText
              dataCy="input-phone"
              id="phoneInvite"
              name="phone"
              type="text"
              maxLength={15}
              disabled={loading}
            />
          </FormBase.InputContent>

          <FormBase.InputContent
            filled={!!values.roles}
            inputRef="select-userType"
            label="User Type"
            errorMessage={errors.roles?.message}
            dataCy="label-userType"
            value={values.roles}
            isSelectData
          >
            <SelectData
              data={rolesAux}
              value={values.roles}
              dataCy="select-userType"
              onValueChange={handleRoleInviteSelectChange}
              zIndex={10}
              ref={selectData}
              onOpenChange={(isOpen) => {
                handleOpenChange(isOpen, 'role');
              }}
              placeholder="User Type"
              disabled={loading}
            />
          </FormBase.InputContent>
        </FormBase.Content>
      </FormModal>
    </FormBase.Provider>
  );
};
